/**
 * USR-03: Cambiar Contraseña Propia
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { KeyRound, Loader2, Eye, EyeOff, ArrowLeft, CheckCircle2 } from 'lucide-react'
import { useChangePassword } from '@/modules/auth/hooks/useAuth'
import { useAuthStore } from '@/store/authStore'

const schema = z.object({
  current_password: z.string().min(1, 'La contraseña actual es requerida'),
  new_password: z.string()
    .min(8, 'La nueva contraseña debe tener al menos 8 caracteres')
    .regex(/[A-Z]/, 'Debe contener al menos una letra mayúscula')
    .regex(/[a-z]/, 'Debe contener al menos una letra minúscula')
    .regex(/[0-9]/, 'Debe contener al menos un número')
    .regex(/[!@#$%^&*(),.?":{}|<>\-_+=[\]~`/\\]/, 'Debe contener al menos un carácter especial'),
  confirm_password: z.string().min(1, 'Confirma tu nueva contraseña'),
}).refine((d) => d.new_password === d.confirm_password, {
  message: 'Las contraseñas no coinciden',
  path: ['confirm_password'],
}).refine((d) => d.new_password !== d.current_password, {
  message: 'La nueva contraseña debe ser diferente a la contraseña actual',
  path: ['new_password'],
})

type FormData = z.infer<typeof schema>

export function ChangePasswordPage() {
  const navigate = useNavigate()
  const { logout } = useAuthStore()
  const { mutate: changePassword, isPending } = useChangePassword()
  const [success, setSuccess] = useState(false)
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  const onSubmit = (data: FormData) => {
    setApiError(null)
    changePassword(data, {
      onSuccess: () => {
        setSuccess(true)
        // Cerrar sesión después de 2 segundos (los tokens se invalidan)
        setTimeout(() => {
          logout()
          navigate('/auth/login')
        }, 2500)
      },
      onError: (error: any) => {
        const data = error?.response?.data
        const msg = data?.error?.message
          || data?.detail
          || data?.new_password?.[0]
          || data?.current_password?.[0]
          || 'Error al cambiar la contraseña. Verifica tu contraseña actual.'
        setApiError(msg)
      },
    })
  }

  if (success) {
    return (
      <div className="p-6 max-w-md mx-auto mt-8">
        <div className="bg-glass backdrop-blur-glass rounded-card shadow-sm border border-divider p-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-success-surface-strong flex items-center justify-center">
              <CheckCircle2 size={32} className="text-success-solid" />
            </div>
          </div>
          <h2 className="text-xl font-semibold text-content mb-2">¡Contraseña actualizada!</h2>
          <p className="text-content-muted text-sm">
            Tu contraseña fue cambiada correctamente. Serás redirigido al login en un momento...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-md mx-auto mt-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-soft text-content-muted transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-xl font-bold text-content">Cambiar contraseña</h1>
          <p className="text-content-muted text-sm">USR-03: Actualiza tu contraseña de acceso</p>
        </div>
      </div>

      <div className="bg-glass backdrop-blur-glass rounded-card shadow-sm border border-divider p-6">
        <div className="flex justify-center mb-5">
          <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
            <KeyRound size={22} className="text-primary" />
          </div>
        </div>

        {apiError && (
          <div className="mb-4 p-3 bg-danger-surface border border-danger-border rounded-lg text-danger-content-soft text-sm">
            {apiError}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Contraseña actual */}
          <div>
            <label htmlFor="changepasswordpage-current-password" className="block text-sm font-medium text-content mb-1">
              Contraseña actual
            </label>
            <div className="relative">
              <input id="changepasswordpage-current-password"
                {...register('current_password')}
                type={showCurrent ? 'text' : 'password'}
                className="w-full px-3 py-2.5 pr-10 border border-divider-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Tu contraseña actual"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content-muted"
              >
                {showCurrent ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            {errors.current_password && (
              <p className="text-danger-solid text-xs mt-1">{errors.current_password.message}</p>
            )}
          </div>

          {/* Nueva contraseña */}
          <div>
            <label htmlFor="changepasswordpage-new-password" className="block text-sm font-medium text-content mb-1">
              Nueva contraseña
            </label>
            <div className="relative">
              <input id="changepasswordpage-new-password"
                {...register('new_password')}
                type={showNew ? 'text' : 'password'}
                className="w-full px-3 py-2.5 pr-10 border border-divider-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Mínimo 8 caracteres"
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content-muted"
              >
                {showNew ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            {errors.new_password && (
              <p className="text-danger-solid text-xs mt-1">{errors.new_password.message}</p>
            )}
          </div>

          {/* Confirmar nueva contraseña */}
          <div>
            <label htmlFor="changepasswordpage-confirm-password" className="block text-sm font-medium text-content mb-1">
              Confirmar nueva contraseña
            </label>
            <div className="relative">
              <input id="changepasswordpage-confirm-password"
                {...register('confirm_password')}
                type={showConfirm ? 'text' : 'password'}
                className="w-full px-3 py-2.5 pr-10 border border-divider-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Repite tu nueva contraseña"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content-muted"
              >
                {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            {errors.confirm_password && (
              <p className="text-danger-solid text-xs mt-1">{errors.confirm_password.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isPending}
            className="w-full py-2.5 bg-primary text-on-primary rounded-lg text-sm font-medium hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 mt-2"
          >
            {isPending ? (
              <><Loader2 size={15} className="animate-spin" /> Cambiando contraseña...</>
            ) : (
              'Cambiar contraseña'
            )}
          </button>
        </form>
      </div>
    </div>
  )
}
